/**
 * AI 画图 (对齐旧版 index.html 的「画图」标签页), 与报价页同一套聊天样式:
 *   描述 → parse-image-request (带最近 6 条对话) → 缺尺寸则反问
 *   → 「正在生成图片…」→ generate-image → 结果卡片 (下载 / 全屏 / 复制提示词)
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Download, Lightbulb, Maximize2, Sparkles, Trash2 } from "lucide-react";
import { Badge, Button, Dialog, Spinner, toast } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, errorDetail } from "@/lib/api-client";
import { useScrollOnViewportResize } from "@/lib/useScrollOnViewportResize";
import { InsufficientCreditsNotice } from "@/features/billing/components/InsufficientCreditsNotice";
import { ChatBubble, ChatRow, bubbleClass } from "@/features/quote/components/ChatBubble";
import { copyWithToast } from "@/features/quote/clipboard";
import { Composer } from "@/features/quote/components/Composer";
import { useGenerateImage, useParseImageRequest } from "@/features/quote/hooks";
import { useImageChatStore } from "./store";
import { downloadImage } from "./download";
import {
  buildImageHistory,
  interpretGenerate,
  interpretParse,
  sizeHint,
  truncatePrompt,
  type ImageMsg,
  type ImageResult,
} from "./flow";

const EXAMPLES = [
  "菜单图片 180cm×50cm，内容：早餐菜单，米粉¥8，面条¥9",
  "门头招牌 200cm×80cm，店名\"小美餐厅\"，红色背景",
  "活动海报 60cm×90cm，主题：夏季促销，清爽风格",
];

/** 请求异常 → 一条 assistant 消息 (不计入对话历史) */
function errorMsg(e: unknown): Omit<ImageMsg, "id"> {
  if (e instanceof ApiError && e.status === 402) {
    return { role: "assistant", kind: "credits", content: "" };
  }
  let content = "网络连接失败，请检查网络后重试";
  if (e instanceof ApiError) content = `服务暂时不可用 (${e.status})`;
  else if (e instanceof DOMException && e.name === "AbortError") content = "请求超时，请稍后重试";
  return { role: "assistant", kind: "error", content };
}

export function ImagePage() {
  const msgs = useImageChatStore((s) => s.msgs);
  const pending = useImageChatStore((s) => s.pending);
  const clear = useImageChatStore((s) => s.clear);
  const parseMutation = useParseImageRequest();
  const generateMutation = useGenerateImage();
  const qc = useQueryClient();

  const [draft, setDraft] = useState("");
  const [zoom, setZoom] = useState<ImageResult | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useScrollOnViewportResize(scrollRef);

  const scrollToEnd = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  // 新消息 / 图片加载完成 (高度变化) 时滚到底
  useEffect(scrollToEnd, [msgs]);

  const handleSend = async (text: string) => {
    const store = useImageChatStore.getState;
    if (store().pending) return;
    store().setPending(true);
    const history = buildImageHistory(store().msgs);
    store().add({ role: "user", kind: "text", content: text, history: text });
    try {
      const parsed = interpretParse(await parseMutation.mutateAsync({ text, history }), text);
      if (parsed.kind === "error") {
        store().add({ role: "assistant", kind: "error", content: parsed.message, history: parsed.message });
        return;
      }
      if (parsed.kind === "ask") {
        store().add({ role: "assistant", kind: "text", content: parsed.message, history: parsed.message });
        return;
      }

      const pid = store().add({
        role: "assistant",
        kind: "pending",
        content: `正在生成图片（${sizeHint(parsed.width_cm, parsed.height_cm)}），请稍候...`,
      });
      try {
        const gen = interpretGenerate(
          await generateMutation.mutateAsync({
            prompt: parsed.prompt,
            width_cm: parsed.width_cm,
            height_cm: parsed.height_cm,
          }),
          parsed.prompt,
        );
        if (gen.kind === "error") {
          store().replace(pid, { role: "assistant", kind: "error", content: gen.message });
        } else {
          store().replace(pid, {
            role: "assistant",
            kind: "result",
            content: "",
            results: gen.results,
            history: gen.history,
          });
        }
      } catch (e) {
        store().replace(pid, errorMsg(e));
      }
    } catch (e) {
      store().add(errorMsg(e));
    } finally {
      store().setPending(false);
      // 计费模式下生成图片会扣余额, 刷新侧边栏余额
      void qc.invalidateQueries({ queryKey: ["me"] });
    }
  };

  const fillExample = (ex: string) => {
    setDraft(ex);
    inputRef.current?.focus();
  };

  return (
    <div className="flex h-full flex-col">
      <h1 className="sr-only">AI 画图</h1>
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl space-y-4 px-3 py-4 md:space-y-5 md:px-4 md:py-8">
          {/* 欢迎语 + 示例 (同旧版, 始终作为第一条消息) */}
          <ChatRow role="assistant">
            <div className={cn(bubbleClass("assistant"), "w-full max-w-xl space-y-3 whitespace-normal")}>
              <p>您好！请描述您想生成的图片内容，并告诉我尺寸（如：180cm×50cm）。</p>
              <div className="space-y-1.5">
                <div className="text-caption font-medium text-muted-foreground">试试这些示例</div>
                {EXAMPLES.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => fillExample(ex)}
                    disabled={pending}
                    className="flex w-full items-start gap-2 rounded-lg border bg-background px-3 py-2 text-left text-caption leading-5 text-foreground transition-colors hover:border-brand/50 hover:bg-primary-soft/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                  >
                    <Sparkles aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                    {ex}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-caption leading-5 text-warning-strong">
                <Lightbulb aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  文字内容多（价格表 / 菜单 / 列表）时，推荐用{" "}
                  <Link to="/template" className="font-semibold underline underline-offset-2 hover:no-underline">模板设计</Link>
                  ：AI 生成网页 → 可视化微调 → 渲染成图片，文字更清晰可控。
                </span>
              </div>
            </div>
          </ChatRow>

          {msgs.map((m) => (
            <MessageView key={m.id} msg={m} onZoom={setZoom} onImageLoad={scrollToEnd} />
          ))}
        </div>
      </div>

      <Composer
        pending={pending}
        onSend={handleSend}
        value={draft}
        onChange={setDraft}
        inputRef={inputRef}
        placeholder="描述内容和尺寸, 如: 海报 60×90cm"
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={clear}
            disabled={pending || msgs.length === 0}
            icon={<Trash2 />}
            className="-ml-2 text-muted-foreground hover:text-foreground"
          >
            清空
          </Button>
        }
      />

      <Dialog
        open={!!zoom}
        onClose={() => setZoom(null)}
        size="full"
        title="图片预览"
        description={zoom ? `${zoom.size} px` : undefined}
        bodyClassName="flex items-center justify-center"
        footer={
          zoom && (
            <>
              <Button variant="outline" icon={<Copy />} onClick={() => void copyWithToast(zoom.prompt, "提示词")}>
                复制提示词
              </Button>
              <DownloadButton src={zoom.src} filename="ai_image" />
            </>
          )
        }
      >
        {zoom && (
          <img src={zoom.src} alt="生成图片" className="max-h-[70dvh] max-w-full rounded-lg object-contain shadow-sm" />
        )}
      </Dialog>
    </div>
  );
}

function MessageView({ msg, onZoom, onImageLoad }: { msg: ImageMsg; onZoom: (r: ImageResult) => void; onImageLoad: () => void }) {
  if (msg.kind === "credits") return <InsufficientCreditsNotice />;
  if (msg.kind === "result" && msg.results) {
    return (
      <ChatRow role="assistant">
        {msg.results.map((r, i) => (
          <ResultCard key={i} result={r} index={i} onZoom={onZoom} onLoad={onImageLoad} />
        ))}
      </ChatRow>
    );
  }
  if (msg.kind === "pending") {
    return (
      <ChatRow role="assistant">
        <div className={cn(bubbleClass("assistant"), "flex items-center gap-2 text-muted-foreground")} role="status">
          <Spinner className="text-primary" />
          {msg.content}
        </div>
      </ChatRow>
    );
  }
  if (msg.kind === "error") return <ChatBubble role="assistant" tone="danger" content={msg.content} />;
  return <ChatBubble role={msg.role} content={msg.content} />;
}

function ResultCard({ result, index, onZoom, onLoad }: {
  result: ImageResult;
  index: number;
  onZoom: (r: ImageResult) => void;
  onLoad: () => void;
}) {
  return (
    <figure className="w-full max-w-sm overflow-hidden rounded-xl border bg-card p-2 shadow-sm">
      <button
        type="button"
        className="group relative block w-full overflow-hidden rounded-lg bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => onZoom(result)}
        title="点击全屏查看"
      >
        <img
          src={result.src}
          alt="生成图片"
          onLoad={onLoad}
          className="mx-auto block max-h-[22rem] w-auto max-w-full cursor-zoom-in object-contain"
        />
        <span
          aria-hidden
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/45 text-white opacity-0 transition-opacity group-hover:opacity-100"
        >
          <Maximize2 className="h-4 w-4" />
        </span>
      </button>
      <figcaption className="space-y-2.5 px-1.5 pb-1 pt-2.5">
        <div className="flex items-start gap-2 text-caption text-muted-foreground">
          <Badge className="shrink-0 tabular-nums text-foreground">{result.size} px</Badge>
          <span className="line-clamp-2 min-w-0 break-words leading-5" title={result.prompt}>
            {truncatePrompt(result.prompt)}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <DownloadButton src={result.src} filename={`ai_image_${index + 1}`} size="sm" />
          <Button variant="outline" size="sm" icon={<Maximize2 />} onClick={() => onZoom(result)}>
            全屏
          </Button>
          <Button variant="outline" size="sm" icon={<Copy />} onClick={() => void copyWithToast(result.prompt, "提示词")}>
            复制提示词
          </Button>
        </div>
      </figcaption>
    </figure>
  );
}

/** 下载按钮: URL 图片走后端同源下载 (带 token), base64 图片本地保存。见 ./download.ts */
function DownloadButton({ src, filename, size }: { src: string; filename: string; size?: "sm" }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      await downloadImage(src, filename);
    } catch (e) {
      toast.error("下载失败", { description: errorDetail(e, "下载失败") });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button size={size} icon={<Download />} loading={busy} onClick={() => void onClick()}>
      下载
    </Button>
  );
}
